import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { connectNotificationStream } from '../lib/notificationStream';
import { createTabCoordinator } from '../lib/notificationTabCoordinator';
import {
  compareRevisions,
  createAttentionAlerter,
  isRevisionAdvance,
  type AttentionAlerter,
} from '../lib/notificationAttentionAlerter';
import { getNewNotifications } from '../lib/notificationApi';
import { apiPost } from '../lib/api';
import { readLocalBinding } from '../lib/notificationPush';
import { isQuietOrSnoozed } from '../lib/notificationAttentionPolicy';
import { notificationKeys } from './useNotifications';
import type { NotificationItem, NotificationPreferenceConfig } from '../types/notification';

const MAX_BACKOFF_MS = 30_000;

export interface RealtimeOptions {
  userId: string | null;
  enabled: boolean;
  /** Live preference config; the latest value is read on every alert. */
  getConfig?: () => NotificationPreferenceConfig | null;
  /** Whether the user is really on a study/practice/submission route now. */
  studying?: boolean;
  /** Show an in-app popup for an item the policy allowed. */
  onToast?: (item: NotificationItem) => void;
  /**
   * Claim the global sound allowance before the sound plays. The alerter consults
   * this BEFORE committing the persisted dedupe key, so a rate-rejected sound
   * never burns the 60 s window. Returning false suppresses `onSound` for that item.
   */
  soundAccepted?: () => boolean;
  /** Play the notification sound (only called when the policy allowed it). */
  onSound?: (volume: number | undefined) => void;
}

function revisionOf(summary: unknown): string | null {
  const rev = (summary as { createdRevision?: string } | null)?.createdRevision;
  return typeof rev === 'string' && rev.length > 0 ? rev : null;
}

/**
 * Owns the SSE connection lifecycle: reconnecting with exponential jitter,
 * refreshing focus/online, and invalidating the summary/list on change. The
 * stream only carries revisions, so we always refetch state rather than trusting
 * the payload as content.
 *
 * Attention is deliberately narrow: it fires only when the created revision
 * actually advanced beyond this tab's own last-alerted baseline, by draining
 * `GET /api/notifications/new` up to a fixed watermark and re-checking every
 * suppression per item. A state-only revision bump (read/archive), a reconcile
 * event, or a reconnect with nothing new refreshes data and stays silent. A
 * reconnect after missed events drains one bounded catch-up burst. A reconnect
 * keeps the watermark in memory for the exact user; a user change tears the
 * stream down.
 */
export function useNotificationRealtime({
  userId,
  enabled,
  getConfig,
  studying = false,
  onToast,
  soundAccepted,
  onSound,
}: RealtimeOptions): void {
  const queryClient = useQueryClient();
  // Two distinct watermarks. `observed` is the newest revision this tab has
  // SEEN (so a state-only echo of the same revision is not re-drained);
  // `alerted` is the newest revision this tab has actually DRAINED for
  // attention, which is what a reconnect must catch up from.
  // `pendingFrom` records the `after` of a drain that started but did not
  // finish: a later event must resume at or below that point so a failed fetch
  // never skips rows it never alerted for.
  const watermarkRef = useRef<{
    userId: string | null;
    observed: string;
    alerted: string;
    pendingFrom: string | null;
  }>({
    userId: null,
    observed: '0',
    alerted: '0',
    pendingFrom: null,
  });
  const coordinatorRef = useRef(createTabCoordinator());
  const configRef = useRef(getConfig);
  configRef.current = getConfig;
  // Callbacks/signals are read through refs so they never re-subscribe the
  // stream; the route-derived studying flag is read fresh at every alert.
  const studyingRef = useRef(studying);
  studyingRef.current = studying;
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const soundAcceptedRef = useRef(soundAccepted);
  soundAcceptedRef.current = soundAccepted;
  const onSoundRef = useRef(onSound);
  onSoundRef.current = onSound;

  useEffect(() => {
    if (!enabled || !userId) return;

    if (watermarkRef.current.userId !== userId) {
      watermarkRef.current = { userId, observed: '0', alerted: '0', pendingFrom: null };
    }

    const controller = new AbortController();
    let cancelled = false;
    let attempt = 0;
    let fallbackTimer: number | undefined;
    let realtimeStarted = false;

    const coordinator = coordinatorRef.current;

    const alerter: AttentionAlerter = createAttentionAlerter({
      fetchNew: (params) => getNewNotifications(params),
      getConfig: () => configRef.current?.() ?? null,
      getOwnCreatedRevision: () => watermarkRef.current.alerted,
      setOwnCreatedRevision: (revision) => {
        watermarkRef.current.alerted = revision;
      },
      coordinator,
      isVisible: () => document.visibilityState === 'visible',
      isStudying: () => studyingRef.current,
      onToast: (item) => onToastRef.current?.(item),
      // Forward the caller's budget claim so a rate-rejected sound is decided
      // BEFORE the persisted key is committed.
      soundAccepted: () => soundAcceptedRef.current?.() ?? true,
      onSound: (volume) => onSoundRef.current?.(volume),
    });

    const invalidate = (summary?: unknown) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.summary(userId) });
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      return revisionOf(summary);
    };

    // Quiet/snooze are re-checked inside the alerter; this only keeps the
    // catch-up path from draining while the user is muted.
    const mutedNow = () => {
      const cfg = configRef.current?.();
      if (!cfg) return false;
      const { quiet, snooze } = isQuietOrSnoozed(cfg, new Date());
      return quiet || snooze;
    };

    /**
     * Serialized attention drain. Every request runs inside ONE promise chain, so
     * overlapping `changed` events can never interleave their watermark updates.
     * Requests are COALESCED to the highest target seen.
     *
     * The start cursor is derived at execution time, not at enqueue time:
     *  - while a window is still owed (`pendingFrom`), resume from it;
     *  - otherwise resume from the last revision this tab actually finished
     *    draining (`alerted`), never from the merely-observed one.
     * A failure leaves `pendingFrom` set so the next request retries the owed
     * rows; a success (including a genuinely EMPTY interval, which still advances
     * the cursor) clears it only if this run's own window is what is stored.
     */
    let drainChain: Promise<void> = Promise.resolve();
    let drainTarget: string | null = null;
    let drainRunning = false;

    const drainStep = async (): Promise<void> => {
      const through = drainTarget;
      drainTarget = null;
      if (cancelled || through == null) return;
      // Resume from the FURTHEST point known to be safe: a window still owed,
      // but never behind what has already been drained successfully. So a
      // failure that overlapped a success retries only the truly missed rows.
      const owed = watermarkRef.current.pendingFrom;
      const start =
        owed != null && compareRevisions(owed, watermarkRef.current.alerted) > 0
          ? owed
          : watermarkRef.current.alerted;
      watermarkRef.current.pendingFrom = start;
      try {
        const result = await alerter.drainTo(through, start);
        // A resolved drain advances the cursor even when it delivered no rows.
        if (result && watermarkRef.current.pendingFrom === start) {
          watermarkRef.current.pendingFrom = null;
        }
      } catch {
        // Owed rows stay pending; the next request retries from `start`.
      }
    };

    const runDrain = (target: string): void => {
      // Coalesce to the HIGHEST target: a later event while a drain is running
      // must not be dropped, it must be issued once the current one settles.
      drainTarget =
        drainTarget != null && compareRevisions(drainTarget, target) > 0 ? drainTarget : target;
      if (drainRunning) return;
      drainRunning = true;
      // Re-check after EVERY step: a target that arrived while `drainStep` was in
      // flight is picked up by the next iteration instead of being consumed and
      // discarded, so a coalesced revision always gets its own request.
      drainChain = drainChain.then(async () => {
        while (drainTarget != null && !cancelled) {
          await drainStep();
        }
        drainRunning = false;
      });
    };

    // Reconnect catch-up must start from the revision this tab last ALERTED for,
    // not from the newest revision it has merely observed: a state-only bump
    // advances `observed` but leaves `alerted` behind, and that gap is exactly
    // the burst of items missed while offline. A pending failed window wins,
    // because it still owes attention.
    const drainCatchUp = (target: string) => {
      if (cancelled || mutedNow()) return;
      runDrain(target);
    };

    const startFallbackPolling = () => {
      if (fallbackTimer) return;
      fallbackTimer = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          queryClient.invalidateQueries({ queryKey: notificationKeys.summary(userId) });
        }
      }, 15_000);
    };

    const connect = async () => {
      if (cancelled) return;
      try {
        await connectNotificationStream({
          signal: controller.signal,
          onReady: (summary) => {
            attempt = 0;
            if (fallbackTimer) {
              window.clearInterval(fallbackTimer);
              fallbackTimer = undefined;
            }
            const revision = invalidate(summary);
            if (!realtimeStarted) {
              // First connection: render the current state without alerting for
              // rows that already existed. Both watermarks start current.
              realtimeStarted = true;
              if (revision) {
                watermarkRef.current.observed = revision;
                watermarkRef.current.alerted = revision;
              }
            } else if (revision) {
              // While disconnected nothing could have been drained, so a ready
              // revision is always at or ahead of `alerted`; remember what we
              // have now observed, then drain (observed, revision] for attention.
              watermarkRef.current.observed = revision;
              // Reconnected after a drop: drain a bounded catch-up burst so
              // items missed while disconnected do not alert as a flood.
              drainCatchUp(revision);
            }
          },
          onChanged: (summary) => {
            const before = watermarkRef.current.observed;
            const revision = invalidate(summary);
            if (revision) watermarkRef.current.observed = revision;
            // State-only bumps (read/archive) never advance createdRevision.
            if (!isRevisionAdvance(before, revision)) return;
            // The queue derives the start cursor from `pendingFrom`/`alerted`, so a
            // failed drain never lets a later event skip the rows it owed.
            runDrain(revision as string);
          },
          onReconcile: (summary) => {
            invalidate(summary);
          },
        });
      } catch {
        // fall through to retry
      }
      if (!cancelled) {
        startFallbackPolling();
        const backoff = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
        const jitter = Math.random() * 1000;
        attempt += 1;
        window.setTimeout(connect, backoff + jitter);
      }
    };

    const onFocusOnline = () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.summary(userId) });
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    };
    window.addEventListener('focus', onFocusOnline);
    window.addEventListener('online', onFocusOnline);

    connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (fallbackTimer) window.clearInterval(fallbackTimer);
      coordinator.release();
      coordinatorRef.current = createTabCoordinator();
      window.removeEventListener('focus', onFocusOnline);
      window.removeEventListener('online', onFocusOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, enabled, queryClient]);

  // Presence heartbeat while the tab is visible. `studying` rides the frozen
  // request body from the shared route signal, so a device that is actively
  // practicing/submitting is visible to the server-side quiet suppression. The
  // latest value is read through a ref so a route change never re-subscribes.
  useEffect(() => {
    if (!enabled || !userId) return;
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      const binding = readLocalBinding();
      const subscriptionId = binding && String(binding.userId) === userId
        ? binding.subscriptionId
        : undefined;
      apiPost('/api/notifications/presence', {
        subscriptionId, foreground: true, studying: studyingRef.current,
      }).catch(() => {});
    };
    beat();
    const timer = window.setInterval(beat, 20_000);
    return () => window.clearInterval(timer);
  }, [userId, enabled]);
}
